import axios from "axios";
import { getOAuthHeaders } from "../constants/oAuthValidation";

const getServerUrl = () =>
  window.__CW_TENANT_URLS__?.REACT_APP_serverUrl || process.env.REACT_APP_serverUrl;

/**
 * Actively verifies the backend is reachable — independent of navigator.onLine.
 *
 * navigator.onLine is unreliable in browsers: it reports `true` whenever a
 * network interface exists (LAN / router / localhost dev server), even when the
 * machine has no real internet and the POS backend is unreachable. This probe
 * closes that gap by making a lightweight authenticated GraphQL request.
 *
 * Reachability requires a genuine GraphQL response from the backend — a bare
 * HTTP 200 is not enough, because a service worker, proxy, or captive portal can
 * return a cached/synthetic 200 that isn't the real server. We therefore require
 * the response body to carry the GraphQL envelope ({ data } or { errors }).
 *   - Valid GraphQL envelope (2xx) => reachable.
 *   - 401/403 => reachable (server answered; token issue is separate).
 *   - Non-GraphQL 200 / other status / connection failure / timeout => unreachable.
 *
 * Only Content-Type + Authorization headers are sent (matching HealthMonitor's
 * checkApiConnectivity), so no new CORS-preflight headers are introduced — POST
 * requests are not HTTP-cached by browsers and the service worker ignores them
 * (it only handles same-origin GET), so no cache-busting is required.
 *
 * @param {number} timeoutMs Request timeout in ms (default 4000).
 * @returns {Promise<boolean>} true if the real backend responded, false otherwise.
 */
export async function isServerReachable(timeoutMs = 4000) {
  const url = getServerUrl();
  if (!url) {
    return false;
  }
  try {
    const authHeaders = getOAuthHeaders();
    const headers = { "Content-Type": "application/json" };
    if (authHeaders?.access_token) {
      headers.Authorization = authHeaders.access_token;
    }
    const res = await axios.post(url, { query: "{ __typename }" }, { headers, timeout: timeoutMs });
    const body = res?.data;
    // A genuine GraphQL response carries a `data` or `errors` field.
    const isGraphQL = !!body && typeof body === "object" && ("data" in body || "errors" in body);
    return res?.status >= 200 && res?.status < 300 && isGraphQL;
  } catch (err) {
    const status = err?.response?.status;
    // 401/403 means the real server answered (auth/token issue is separate).
    return status === 401 || status === 403;
  }
}
